import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '../ui/Icon';
import Button from '../ui/Button';
import FieldError from '../ui/FieldError';
import { ApiError } from '../../api/client';
import { feedbackApi } from '../../api/feedback';
import { formatBytes } from './feedbackAdapter';
import { ACCEPT, checkFiles, greeting, hasUnsentText, LIMITS, mapReplyErrors, MAX_FILES, validateReply } from './replyRules';
import useReplyAttachments from './useReplyAttachments';

const inputBase = 'w-full px-3 py-2 rounded-lg text-[13px] text-on-surface border outline-none transition placeholder:text-on-surface-variant/50';
const inputOk = 'bg-surface-container-lowest border-outline-variant/50 focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';
const inputInvalid = 'bg-status-alert-bg/40 border-error focus:shadow-[0_0_0_3px_rgba(186,26,26,0.15)]';

function AttachmentChip({ item, onRemove, onRetry }) {
  const tone =
    item.status === 'error'
      ? 'border-status-alert-border bg-status-alert-bg text-status-alert-text'
      : item.status === 'ready'
        ? 'border-status-ok-border bg-status-ok-bg/60 text-on-surface'
        : 'border-outline-variant/40 bg-surface-container-low text-on-surface';
  return (
    <li className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg border text-xs ${tone}`}>
      <Icon
        name={item.status === 'uploading' ? 'progress_activity' : item.status === 'error' ? 'error' : /\.pdf$/i.test(item.name) ? 'picture_as_pdf' : 'image'}
        size={16}
        className={item.status === 'uploading' ? 'animate-spin text-secondary' : ''}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-data-mono" title={item.name}>
          {item.name}
        </span>
        <span className="block text-[10px] text-on-surface-variant">
          {formatBytes(item.size)}
          {' · '}
          {item.status === 'uploading' ? 'Uploading…' : item.status === 'ready' ? 'Ready' : item.error || 'Upload failed'}
        </span>
      </span>
      {item.status === 'error' && (
        <button
          type="button"
          onClick={() => onRetry(item.key)}
          className="px-1.5 py-0.5 rounded font-semibold underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          Retry
        </button>
      )}
      <button
        type="button"
        onClick={() => onRemove(item.key)}
        aria-label={`Remove ${item.name}`}
        className="p-1 rounded text-on-surface-variant hover:text-on-surface hover:bg-surface-container outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
      >
        <Icon name="close" size={14} />
      </button>
    </li>
  );
}

/**
 * Email reply to the customer. `prefill` (from "Send again") replaces subject, body and
 * attachments; `onUpdated` gets the ticket returned by the server (sent or not delivered).
 */
export default function ReplyForm({ ticket, prefill, onUpdated, onDirtyChange }) {
  const defaults = { subject: ticket.replyDefaults.subject, body: greeting(ticket.customer.firstName) };
  const [subject, setSubject] = useState(defaults.subject);
  const [body, setBody] = useState(defaults.body);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [rejected, setRejected] = useState([]);
  const [attempted, setAttempted] = useState(false);
  const [noEmail, setNoEmail] = useState(!ticket.replyDefaults.to);
  const attachments = useReplyAttachments();
  const fileRef = useRef(null);
  const alertRef = useRef(null);

  const dirty = hasUnsentText({ subject, body, attachments: attachments.items }, defaults);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  const { reset: resetAttachments } = attachments;
  useEffect(() => {
    if (!prefill) return;
    setSubject(prefill.subject);
    setBody(prefill.body);
    resetAttachments(prefill.attachments);
    setErrors({});
    setGeneralError(null);
    requestAnimationFrame(() => document.getElementById('reply-body')?.focus());
  }, [prefill, resetAttachments]);

  const revalidate = (next) => attempted && setErrors((e) => ({ ...validateReply(next), attachmentUrls: e.attachmentUrls }));

  const send = useMutation({
    mutationFn: (payload) => feedbackApi.sendReply(ticket.id, payload),
    onSuccess: (result) => {
      onUpdated(result.feedback);
      if (result.delivered) {
        toast.success(`Reply sent to ${ticket.replyDefaults.to}`);
        setSubject(result.feedback.replyDefaults?.subject || defaults.subject);
        setBody(defaults.body);
        attachments.reset();
        setAttempted(false);
        setErrors({});
        setGeneralError(null);
      } else {
        // 502: saved, but the email didn't go out; keep the text so it can be sent again
        const message = result.message || 'The reply was saved but the email could not be sent. Try again.';
        setGeneralError(message);
        toast.error(message);
        requestAnimationFrame(() => alertRef.current?.focus());
      }
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
      if (err?.status === 409 && err.data?.Data?.code === 'NO_CUSTOMER_EMAIL') {
        setNoEmail(true);
        toast.error('This customer has no email address');
        return;
      }
      if (err?.status === 400 && err.fieldErrors?.length) {
        const mapped = mapReplyErrors(err.fieldErrors);
        setErrors(mapped.errors);
        setGeneralError(mapped.general.length ? mapped.general.join(' ') : null);
        const first = ['subject', 'body'].find((f) => mapped.errors[f]);
        if (first) requestAnimationFrame(() => document.getElementById(`reply-${first}`)?.focus());
      } else {
        setGeneralError(message);
      }
      toast.error(message);
    },
  });

  const submit = () => {
    if (send.isPending || attachments.uploading || noEmail) return;
    setAttempted(true);
    const found = validateReply({ subject, body });
    setErrors(found);
    setGeneralError(null);
    const first = ['subject', 'body'].find((f) => found[f]);
    if (first) {
      document.getElementById(`reply-${first}`)?.focus();
      return;
    }
    send.mutate({
      ...(subject.trim() && { subject: subject.trim() }),
      body,
      ...(attachments.readyUrls.length && { attachmentUrls: attachments.readyUrls }),
    });
  };

  const onPick = (e) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    const { accepted, rejected: reasons } = checkFiles(picked, attachments.items.length);
    setRejected(reasons);
    setErrors((x) => ({ ...x, attachmentUrls: undefined }));
    if (accepted.length) attachments.add(accepted);
  };

  const disabled = noEmail || send.isPending;
  const failedUploads = attachments.items.filter((i) => i.status === 'error').length;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex flex-col gap-3"
      aria-label="Reply to customer"
    >
      {noEmail ? (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2.5 text-xs text-status-alert-text">
          <Icon name="unsubscribe" size={16} />
          This customer has no email address
        </div>
      ) : (
        generalError && (
          <div
            ref={alertRef}
            role="alert"
            tabIndex={-1}
            className="flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2.5 text-xs text-status-alert-text outline-none"
          >
            <Icon name="error" size={16} className="shrink-0" />
            {generalError}
          </div>
        )
      )}

      {ticket.customer.isGuest && !noEmail && (
        <p className="flex items-start gap-2 rounded-lg border border-status-pending-border bg-status-pending-bg px-3 py-2.5 text-xs text-status-pending-text">
          <Icon name="info" size={16} className="shrink-0" />
          This visitor has no Pompi account, so they can only read your reply by email. Check the address before sending.
        </p>
      )}

      <p className="text-xs text-on-surface-variant flex flex-wrap items-baseline gap-x-1.5">
        From:
        <span className="font-data-mono font-semibold text-on-surface break-all">{ticket.replyDefaults.from || 'Pompi Support'}</span>
      </p>

      <div>
        <div className="flex items-baseline justify-between mb-1">
          <label htmlFor="reply-subject" className="text-xs font-bold text-on-surface">
            Subject
          </label>
          <span className="font-data-mono text-[10px] text-on-surface-variant">
            {subject.length}/{LIMITS.subject}
          </span>
        </div>
        <input
          id="reply-subject"
          type="text"
          value={subject}
          disabled={disabled}
          onChange={(e) => {
            setSubject(e.target.value);
            revalidate({ subject: e.target.value, body });
          }}
          aria-invalid={Boolean(errors.subject)}
          aria-describedby={errors.subject ? 'reply-subject-error' : undefined}
          className={`${inputBase} ${errors.subject ? inputInvalid : inputOk} disabled:opacity-60`}
        />
        <FieldError id="reply-subject-error">{errors.subject}</FieldError>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1">
          <label htmlFor="reply-body" className="text-xs font-bold text-on-surface">
            Message Body
          </label>
          <span className={`font-data-mono text-[10px] ${body.length > LIMITS.body ? 'text-error' : 'text-on-surface-variant'}`}>
            {body.length} / {LIMITS.body}
          </span>
        </div>
        <textarea
          id="reply-body"
          rows={8}
          value={body}
          disabled={disabled}
          onChange={(e) => {
            setBody(e.target.value);
            revalidate({ subject, body: e.target.value });
          }}
          aria-invalid={Boolean(errors.body)}
          aria-describedby={errors.body ? 'reply-body-error' : undefined}
          className={`${inputBase} ${errors.body ? inputInvalid : inputOk} resize-y min-h-32 leading-relaxed disabled:opacity-60`}
        />
        <FieldError id="reply-body-error">{errors.body}</FieldError>
      </div>

      <div>
        <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={onPick} data-testid="reply-file-input" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || attachments.items.length >= MAX_FILES}
          aria-describedby="reply-files-hint"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-container text-xs font-semibold text-on-surface hover:bg-surface-container-high transition disabled:opacity-50 disabled:cursor-not-allowed outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          <Icon name="attach_file" size={16} />+ Attach file / proof
        </button>
        <p id="reply-files-hint" className="mt-1 text-[11px] text-on-surface-variant">
          JPG, PNG or PDF, up to 10 MB each, max {MAX_FILES} files. Sent to the customer as links.
        </p>
        {attachments.items.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1.5" aria-label="Attached files">
            {attachments.items.map((item) => (
              <AttachmentChip key={item.key} item={item} onRemove={attachments.remove} onRetry={attachments.retry} />
            ))}
          </ul>
        )}
        {rejected.length > 0 && (
          <ul role="alert" className="mt-2 space-y-0.5 text-[11px] text-error">
            {rejected.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        {failedUploads > 0 && <p className="mt-1 text-[11px] text-status-pending-text">Files that failed to upload won&apos;t be attached.</p>}
        <FieldError id="reply-attachments-error">{errors.attachmentUrls}</FieldError>
      </div>

      <div className="pt-3 border-t border-surface-container flex justify-end">
        <Button type="submit" size="lg" icon={send.isPending ? undefined : 'send'} disabled={disabled || attachments.uploading || !body.trim()}>
          {send.isPending ? (
            <>
              <Icon name="progress_activity" size={20} className="animate-spin" />
              Sending…
            </>
          ) : (
            'Send Email Reply'
          )}
        </Button>
      </div>
    </form>
  );
}
